import * as React from 'react';
import GameBlock from '../../GameBlock/GameBlock.tsx';
import GameModal from '../../GameModal/GameModal.tsx';
import NextGameCountdown from '../../NextGameCountdown/NextGameCountdown.js';
import {getDisplayStatsForGameNumber} from '../../../utils/userStatsUtils.ts';
import useGameNavigation from '../../../utils/useGameNavigation.ts';
import { getGameConfig } from '../../../types/GameConfig.ts';
import { SubtextStatsModalProps } from './types';
import { getPlayOtherGameCards, SignInPrompt } from './sharedComponents.tsx';

import './GameStatsModal.css';

const SubtextStatsModal: React.FC<SubtextStatsModalProps> = (props) => {
  const gameNavigation = useGameNavigation(props.gameType);

  // Get strongly-typed display stats for Subtext games
  const displayStats = getDisplayStatsForGameNumber(props.gameType, props.gameNumber);



  function getArchiveActionButtons(): React.ReactElement {
    const gameConfig = getGameConfig(props.gameType);
    const nextGameNumber = props.gameNumber + 1;
    const hasNextGame = gameConfig.isValidGameNumber(nextGameNumber);
    const nextLabel = hasNextGame
      ? `Play next ${gameConfig.name} (#${nextGameNumber})`
      : `Play today's ${gameConfig.name}`;

    return (
      <>
        <div
          className='playOtherGameCard'
          onClick={props.onPlayNext}
          style={{ backgroundColor: gameConfig.primaryColor }}
        >
          <div className='playOtherGameButtonText'>
            {nextLabel}
          </div>
        </div>
        <div className='playOtherGameCard playOtherGameCardToday' onClick={props.onReturnToArchive}>
          <div className='playOtherGameButtonText'>
            Return to Archives
          </div>
        </div>
      </>
    );
  }

  function shareSubtextResults(): void {
    const gameConfig = getGameConfig(props.gameType);
    const gameName = gameConfig.name;
    
    let text = `${gameName} #${props.gameNumber}\n\n`;
    
    // Use actual daily word and letter counts from display stats
    const dailyWord = displayStats.dailyWord;
    const letterCounts = displayStats.letterCounts;
    
    // Create dots based on letter counts
    const processedLetters = new Set<string>();
    
    for (const letter of dailyWord) {
      let count = 0;
      if (!processedLetters.has(letter)) {
        count = letterCounts[letter] || 0;
        processedLetters.add(letter);
      }
      
      const dots = count > 0 ? '· '.repeat(count) : '';
      text += count > 0 ?`${letter.toUpperCase()} ${dots}${count}\n` : `${letter.toUpperCase()}\n`;
    }
    
    text += '\n';
    if (displayStats.todayRank) {
      if (displayStats.foundAllWords) {
        text += `Rank: ${displayStats.foundAllWordsRankName}`;
      } else {
        text += `Rank: ${displayStats.todayRank}`;
      }
    }
    
    if (navigator.canShare && navigator.canShare({text: text})) {
      navigator.share({ text: text })
        .then(() => {
          props.onCopyToClipboard();
        })
        .catch(() => {
          copyToClipboard(text);
        });
    } else {
      copyToClipboard(text);
    }
  }

  function copyToClipboard(text: string): void {
    var dummy = document.createElement("textarea");
    document.body.appendChild(dummy);
    dummy.value = text;
    dummy.select();
    document.execCommand("copy");
    document.body.removeChild(dummy);
    props.onCopyToClipboard();
  }

  function getTodayModalContent() {
    const gameConfig = getGameConfig(props.gameType);
    
    return (
      <div id='gameStatsModalContent' className='subtextStatsModalContent'>
        {/* First row: Today's Rank (big and bold) */}
        {displayStats.todayRank && (
          <div className='statsModalRow'>
            <div className='subtextRankContainer'>
              <div className='subtextTodayRankDisplay'>
                {displayStats.foundAllWords ? displayStats.foundAllWordsRankName : displayStats.todayRank}
              </div>
              {displayStats.foundAllWords && (
                <div className='subtextFoundAllWordsRankLabel'>
                  You found everything!
                </div>
              )}
            </div>
          </div>
        )}

        {/* Second row: Words, Points, Share */}
        <div className='statsModalRow'>
          <div className='userStat'>
            <GameBlock 
              character={displayStats.todayWordCount?.toString() ?? '?'} 
              classNames={displayStats.todayWordCount != null ? ['successUserStatsBlock'] : ['userStatsBlock']} 
            />
            <div className='userStatLabel'>
              Today's Words
            </div>
          </div>
          <div className='userStat'>
            <GameBlock 
              character={displayStats.todayPoints?.toString() ?? '?'} 
              classNames={displayStats.todayPoints != null ? ['successUserStatsBlock'] : ['userStatsBlock']} 
            />
            <div className='userStatLabel'>
              Today's Points
            </div>
          </div>
          {displayStats.todayWordCount != null && displayStats.todayPoints != null && (
            <div className='shareTodaysScoreButtonContainer'>
              <button className='shareTodaysScoreButton' onClick={shareSubtextResults}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92zM18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 7.02c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
                </svg>
                <div className='shareButtonText'>
                  Share
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Third row: Total stats */}
        <div className='statsModalRow'>
          <div className='userStat'>
            <GameBlock character={displayStats.totalWordsFound.toString()} classNames={['userStatsBlock']} />
            <div className='userStatLabel'>
              Words
            </div>
          </div>
          <div className='userStat'>
            <GameBlock character={displayStats.totalGamesPlayed.toString()} classNames={['userStatsBlock']} />
            <div className='userStatLabel'>
              Puzzles
            </div>
          </div>
          <div className='userStat'>
            <GameBlock character={displayStats.highRankCount.toString()} classNames={['userStatsBlock']} />
            <div className='userStatLabel'>
              {displayStats.highRankName}
            </div>
          </div>
        </div>

        {/* Next Game Countdown */}
        <div className='statsModalRow'>
          <div className='nextGameCountdownContainer'>
            <NextGameCountdown />
            <div>Next {gameConfig.name}</div>
          </div>
        </div>

        {/* Other Games */}
        {getPlayOtherGameCards({
          currentGameType: props.gameType,
          onGameSelect: (gameType) => gameNavigation.redirectToGame(gameType, 'game_stats_modal')
        })}
        <SignInPrompt onClose={props.onClose} />
      </div>
    );
  }

  function getArchivedModalContent() {
    return (
      <div id='gameStatsModalContent' className='subtextStatsModalContent'>
        {/* First row: Rank (big and bold) */}
        {displayStats.todayRank && (
          <div className='statsModalRow'>
            <div className='subtextRankContainer'>
              <div className='subtextTodayRankDisplay'>
                {displayStats.foundAllWords ? displayStats.foundAllWordsRankName : displayStats.todayRank}
              </div>
              {displayStats.foundAllWords && (
                <div className='subtextFoundAllWordsRankLabel'>
                  You found everything!
                </div>
              )}
            </div>
          </div>
        )}

        {/* Second row: Words and Points */}
        <div className='statsModalRow'>
          <div className='userStat'>
            <GameBlock 
              character={displayStats.todayWordCount?.toString() ?? '?'} 
              classNames={displayStats.todayWordCount != null ? ['successUserStatsBlock'] : ['userStatsBlock']} 
            />
            <div className='userStatLabel'>
              Words
            </div>
          </div>
          <div className='userStat'>
            <GameBlock 
              character={displayStats.todayPoints?.toString() ?? '?'} 
              classNames={displayStats.todayPoints != null ? ['successUserStatsBlock'] : ['userStatsBlock']} 
            />
            <div className='userStatLabel'>
              Points
            </div>
          </div>
        </div>
        {getArchiveActionButtons()}
      </div>
    );
  }

  function getModalContent() {
    const gameConfig = getGameConfig(props.gameType);
    if (gameConfig.isGameNumberCurrentGame(props.gameNumber)) {
      return getTodayModalContent();
    } else {
      return getArchivedModalContent();
    }
  }

  return (
    <GameModal
      title='Statistics'
      content={getModalContent()}
      isOpen={props.isOpen}
      onClose={props.onClose}
      modalStyle={{ paddingBottom: 0 }}
    />
  );
};

export default SubtextStatsModal; 